// tslint:disable max-line-length
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TenantsService } from '@app/clients/tenants';
import { ServiceProviderProperties, Tenant, TenantCreateParamsNew } from '@app/clients/tenants/gen';
import { ModelError } from '@app/clients/tenants/gen/model/error';
import { Observable } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { BAD_REQUEST_MP_ACCNT_ID_NOT_FOUND, CustomMode, CustomProduct, CustomProductToMode } from '@app/app.constants';

/**
 * Shared service that manages the global state needed by various routes.
 *
 * Angular forces us to do this, since a shared service is apparently the only way to share data
 * between different routes without using the URL.
 */
@Injectable()
export class ContextService {
  marketplaceAccountId?: string;
  product?: CustomProduct;

  // This property is used to identify whether the signup from Google Marketplace is complete or not
  private incompleteSignup?: boolean;
  private loadingObservable: Observable<void>;
  private contextObservable: Observable<ServiceProviderProperties>;
  private signupModel: TenantCreateParamsNew = newSignupModel();
  private signupObservable: Observable<Tenant>;
  private error: WrappedError;

  constructor(
    private tenantsService: TenantsService
  ) {
    // Note: we want to cache & replay one Ajax response for each observable below regardless of how many
    // subscribe()rs are added. shareReplay(1) does this but does not replay errors. So if an error
    // is thrown it will be retried N times for N subscribers. TODO find a solution

    // Signup
    this.contextObservable = (tenantsService.getProperties('body') as Observable<ServiceProviderProperties>)
      .pipe(catchError((err: BackendHttpErrorResponse) => {
        this.error = (err.status === 404) ? new SignupDisabledError(err) : wrap(err);
        throw this.error;
      }))
      .pipe(shareReplay(1));
    this.loadingObservable = voidify(this.contextObservable);
  }

  /**
   * @returns An observable that completes when the initial data has been fetched and the page
   * should be rendered. The shell displays a spinner until this observable has completed.
   *
   * This meaning of "initial data" depends on what flow we are in (signup or verify).
   */
  getLoadingObservable(): Observable<void> {
    return this.loadingObservable;
  }

  /**
   * @returns The tenant context
   */
  getTenantContext(): Observable<ServiceProviderProperties> {
    return this.contextObservable;
  }

  /**
   * @returns The singup model. Routes may mutate this object, e.g. through data bindings.
   */
  getSignupModel(): TenantCreateParamsNew {
    return this.signupModel;
  }

  /**
   * @returns Observable for the signup response.
   */
  getSignup(): Observable<Tenant> {
    return this.signupObservable;
  }

  isSignupIncomplete() {
    return this.incompleteSignup;
  }

  /**
   * Send the request to signup the tenant.
   */
  signup() {
    const referer = ''; // generated code forces us to pass in Referer although it can't actually be changed
    this.signupObservable = this.tenantsService.signUpTenant(referer, this.getSignupModel())
      .pipe(catchError((err: BackendHttpErrorResponse) => {
        this.incompleteSignup = false;
        switch (err.status) {
          case 400:
            // For errors with status 400, if the error message contains the particular text then it is marked as incomplete signup
            // It is a hacky way for now using the error message as there is no other clear way to identify incomplete signup
            if (err.error?.message?.toLocaleLowerCase().includes(BAD_REQUEST_MP_ACCNT_ID_NOT_FOUND)) {
              this.incompleteSignup = true;
            }
            this.error = new BadRequestError(err);
            break;
          case 403:
            this.error = new UnauthorizedRequestError(err);
            break;
          case 404:
            this.error = new BlockedError(err);
            break;
          case 503:
            this.error = new ServiceUnavailableError(err);
            break;
          case 500:
            // fallthrough
          default:
            this.error = wrap(err);
        }
        throw this.error;
      }));
  }

  /**
   * @returns The last error that occurred
   */
  getError(): WrappedError {
    return this.error;
  }

  getCustomMode(): CustomMode | undefined {
    return this.marketplaceAccountId && CustomProductToMode[this.product] || undefined;
  }
}

/**
 * Just converts an observable that emits anything to one that only emits void
 */
function voidify(o: Observable<any>): Observable<void> {
  return o.pipe(map(t => undefined));
}

function newSignupModel(): TenantCreateParamsNew {
  const model: TenantCreateParamsNew = {
    companyDepartment: '', companyName: '',
    name: '',
    billingAddress: {
      country: '',
      state: '',
      streetAddress: '',
      city: '',
      zip: '',
    },
    adminGivenName: '',
    adminFamilyName: '',
    adminPhoneNumber: '',
    adminEmail: '',
    captcha: '',
    marketplaceAccountId: ''
  };
  return model;
}

/**
 * Error indicating that onboarding is disabled for the service provider
 */
export class SignupDisabledError extends Error {
  constructor(public source: BackendHttpErrorResponse) {
    super('Signup disabled');
    this.source = source;

    // https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, SignupDisabledError.prototype);
  }
}

export class BadRequestError extends Error {
  constructor(public source: BackendHttpErrorResponse) {
    super('Bad request');
    this.source = source;
    Object.setPrototypeOf(this, BadRequestError.prototype);
  }
}

export class UnauthorizedRequestError extends Error {
  constructor(public source: BackendHttpErrorResponse) {
    super('Unauthorized request');
    this.source = source;
    Object.setPrototypeOf(this, UnauthorizedRequestError.prototype);
  }
}

export class BlockedError extends Error {
  constructor(public source: BackendHttpErrorResponse) {
    super('Blocked');
    this.source = source;
    Object.setPrototypeOf(this, BlockedError.prototype);
  }
}

/**
 * Error indicating that the system is unavailable or could not complete the request
 * within the expected time.
 */
export class ServiceUnavailableError extends Error {
  constructor(public source: BackendHttpErrorResponse) {
    super('Service Unavailable');
    this.source = source;
    Object.setPrototypeOf(this, ServiceUnavailableError.prototype);
  }
}

/**
 * Common interface implemented by the specific error types, so clients can get at the
 * underlying HTTP response returned from the server.
 */
export interface WrappedError extends Error {
  source: BackendHttpErrorResponse;
}

function wrap(err: BackendHttpErrorResponse): WrappedError {
  return {
    source: err,
    name: err.name,
    message: err.message
  };
}

export interface BackendHttpErrorResponse extends HttpErrorResponse {
  error: ModelError;
}
